/**
 * session.js — the pure half of MHI's VarahiID sign-in (2026-09-24).
 *
 * No import.meta, no React, no globals read at import time: every browser API
 * is passed in, so plain `node src/auth/__tests__/session.test.js` can run it.
 * AuthProvider.jsx is the only caller that wires in window/fetch/caches.
 *
 * The gate (access-gate, /varahi-mhi-access) answers GET /session with
 *   200 {sub,email,name,tid,tslug,roles,perms,mfa,expires_at,mode}
 *   401 when there is no live session.
 * It never returns tokens. Anything else (a 5xx, a 404 while the gate is not
 * installed, an HTML page, a network error while offline) is "unknown": the
 * SPA keeps whatever it already showed, so the offline / Pi demo keeps working.
 */

// Same key and window as Happen's auto sign-in (LoginPage.tsx, happen_auto_sso_at):
// at most one automatic trip to the gate per 30 s per tab, so a gate or VarahiID
// that keeps bouncing back can never become a redirect loop. VarahiID's
// /id/signed-out clears this key (DEFAULT_STORAGE_KEYS in src/login/signout.ts).
export const AUTO_SSO_KEY = 'mhi_auto_sso_at';
export const AUTO_SSO_WINDOW_MS = 30_000;

// Service-worker caches this app owns (vite.config.js runtimeCaching names).
// The origin is shared with Happen, Converse, GetScreened and One, so a sign-out
// or 401 must delete only these — never every cache on project.varahitechnologies.com.
export const OWN_CACHE_NAMES = Object.freeze(['app-shell', '3d-models', 'api-cache']);

/** @returns {'ok'|'unauth'|'unknown'} from a fetch Response, or from a thrown network error. */
export function classify(result) {
  if (!result || result instanceof Error || typeof result.status !== 'number') return 'unknown';
  if (result.status === 200) return 'ok';
  if (result.status === 401) return 'unauth';
  return 'unknown';
}

const str = (v) => (typeof v === 'string' ? v : '');
const strList = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x) : []);

/** Validate the gate's /session JSON. Returns a frozen user, or null when it is not a session. */
export function normalizeSession(json) {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null;
  const sub = str(json.sub).trim();
  if (!sub) return null;
  const expires = typeof json.expires_at === 'number' || typeof json.expires_at === 'string'
    ? json.expires_at : null;
  return Object.freeze({
    sub,
    email: str(json.email),
    name: str(json.name),
    tid: str(json.tid),
    tslug: str(json.tslug),
    roles: Object.freeze(strList(json.roles)),
    perms: Object.freeze(strList(json.perms)),
    mfa: json.mfa === true,
    expiresAt: expires,
    mode: str(json.mode) || 'varahiid',
  });
}

/**
 * Ask the gate who is signed in.
 * @returns {Promise<{kind:'ok',user:object}|{kind:'unauth'|'unknown',user:null}>}
 */
export async function fetchSession(fetchImpl, accessBase) {
  let res;
  try {
    res = await fetchImpl(`${accessBase}/session`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { accept: 'application/json' },
    });
  } catch (err) {
    return { kind: classify(err instanceof Error ? err : new Error(String(err))), user: null };
  }
  const kind = classify(res);
  if (kind !== 'ok') return { kind, user: null };
  // A 200 that is not the gate's JSON (e.g. a catch-all HTML page while the gate
  // route does not exist yet) is not a session — and not a sign-out either.
  const type = (res.headers && typeof res.headers.get === 'function' && res.headers.get('content-type')) || '';
  if (!/\bjson\b/i.test(type)) return { kind: 'unknown', user: null };
  let body;
  try { body = await res.json(); } catch { return { kind: 'unknown', user: null }; }
  const user = normalizeSession(body);
  return user ? { kind: 'ok', user } : { kind: 'unknown', user: null };
}

/** True when an automatic redirect to the gate is allowed (none in the last 30 s). */
export function shouldRedirect(lastAt, now, windowMs = AUTO_SSO_WINDOW_MS) {
  const last = Number(lastAt);
  if (!Number.isFinite(last) || last <= 0) return true;
  const elapsed = now - last;
  // A timestamp in the future (clock moved back) must not block sign-in forever.
  return !(elapsed >= 0 && elapsed < windowMs);
}

export function readGuard(storage) {
  try { return Number(storage?.getItem(AUTO_SSO_KEY) || 0); } catch { return 0; }
}

export function markGuard(storage, now) {
  try { storage?.setItem(AUTO_SSO_KEY, String(now)); } catch { /* storage blocked: the gate's ?error= page is the other guard */ }
}

/** The gate's login URL, returning to where the person was (path + query, same app). */
export function loginUrl(accessBase, loc) {
  const next = `${loc?.pathname || '/'}${loc?.search || ''}`;
  return `${accessBase}/login?next=${encodeURIComponent(next)}`;
}

/** True for a cache this app owns: a runtime cache by name, or a workbox cache for our scope. */
export function isOwnCache(name, scopeUrl) {
  if (OWN_CACHE_NAMES.includes(name)) return true;
  return !!scopeUrl && name.startsWith('workbox-') && name.endsWith(scopeUrl);
}

/**
 * Delete this app's service-worker caches and unregister its service worker, so
 * the next load cannot come from a precached shell behind forward_auth's back.
 * Never throws; gives up after `timeoutMs` so sign-out can never hang on it.
 *
 * @param {{cachesApi?:CacheStorage, serviceWorker?:ServiceWorkerContainer, scopeUrl?:string, timeoutMs?:number}} deps
 *   scopeUrl — absolute app scope, e.g. https://project.varahitechnologies.com/varahi-mhi/
 */
export async function clearAllCaches({ cachesApi, serviceWorker, scopeUrl, timeoutMs = 2000 } = {}) {
  const out = { deleted: [], unregistered: 0 };
  const work = (async () => {
    if (cachesApi && typeof cachesApi.keys === 'function') {
      try {
        const names = await cachesApi.keys();
        for (const name of names) {
          if (!isOwnCache(name, scopeUrl)) continue;
          try { if (await cachesApi.delete(name)) out.deleted.push(name); } catch { /* keep going */ }
        }
      } catch { /* caches unavailable (private mode): nothing to clear */ }
    }
    if (serviceWorker && typeof serviceWorker.getRegistrations === 'function') {
      try {
        const regs = await serviceWorker.getRegistrations();
        for (const reg of regs) {
          if (scopeUrl && !String(reg.scope || '').startsWith(scopeUrl)) continue;
          try { if (await reg.unregister()) out.unregistered += 1; } catch { /* keep going */ }
        }
      } catch { /* no service worker support */ }
    }
  })();
  let timer;
  const cap = new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  try { await Promise.race([work, cap]); } finally { clearTimeout(timer); }
  return out;
}

/**
 * What to do when the gate says "no session" (401).
 * Always clears this app's caches (a sign-out in another Varahi app lands here
 * too). Then sends the browser to the gate's login unless it already did so in
 * the last 30 s in this tab.
 * @returns {Promise<'redirected'|'guarded'>}
 */
export async function handleUnauthenticated({ now, storage, location, accessBase, clearCaches, assign }) {
  try { await clearCaches(); } catch { /* never block the redirect on cache cleanup */ }
  if (!shouldRedirect(readGuard(storage), now)) return 'guarded';
  markGuard(storage, now);
  assign(loginUrl(accessBase, location));
  return 'redirected';
}

/** Two-letter initials for the avatar. */
export function initialsFor(user) {
  const source = (user?.name || user?.email || '').trim();
  if (!source) return '?';
  const words = source.split('@')[0].split(/[\s._-]+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[words.length - 1][0] : (words[0] || source).slice(0, 2);
  return letters.toUpperCase();
}

/** The role shown on the chip: the most senior one the person holds. */
export function primaryRole(roles) {
  const order = ['owner', 'admin', 'operator', 'viewer'];
  const list = Array.isArray(roles) ? roles : [];
  return order.find((r) => list.includes(r)) || list[0] || '';
}
